import React from 'react';
import { ParamTooltip } from './ParamTooltip';

function DisplayToggle({
  section,
  field,
  draft,
  updateDisplay,
  label,
  tooltipKey,
  defaultEnabled = true,
}) {
  const sectionValue = draft.display?.[section]?.[field];
  const checked = defaultEnabled ? sectionValue !== false : sectionValue === true;
  return (
    <div className="col-md-4 mb-2">
      <label>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => updateDisplay(section, field, e.target.checked)}
        />
        {' '}
        <ParamTooltip paramKey={tooltipKey}>
          {label}
        </ParamTooltip>
      </label>
    </div>
  );
}

function DisplayNumber({
  section,
  field,
  draft,
  updateDisplay,
  label,
  tooltipKey,
  step = 'any',
  allowEmpty = false,
}) {
  const value = draft.display?.[section]?.[field];
  return (
    <div className="col-md-4 mb-2">
      <label>
        <ParamTooltip paramKey={tooltipKey}>
          {label}
        </ParamTooltip>
      </label>
      <input
        type="number"
        step={step}
        className="form-control form-control-sm"
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => {
          if (e.target.value === '') {
            updateDisplay(section, field, allowEmpty ? null : '');
            return;
          }
          const n = Number(e.target.value);
          updateDisplay(section, field, Number.isFinite(n) ? n : null);
        }}
      />
    </div>
  );
}

export function DisplayParamsSection({ draft, updateDisplay, t }) {
  if (!draft.display) {
    return null;
  }

  return (
    <section id="match-params-display" className="match-param-section">
      <h5>{t('match.section.display.title')}</h5>
      <p className="match-param-section-desc">{t('match.section.display.desc')}</p>

      <h6 className="mt-2">{t('match.section.display.badges')}</h6>
      <div className="row">
        <DisplayToggle section="expertBadge" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayExpertBadge" label={t('match.display.expertBadge')} />
        <DisplayToggle section="newHugoBadge" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayNewHugoBadge" label={t('match.display.newHugoBadge')} />
        <DisplayToggle section="coverageBadge" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCoverageBadge" label={t('match.display.coverageBadge')} />
        <DisplayToggle section="speedBadge" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displaySpeedBadge" label={t('match.display.speedBadge')} />
        <DisplayToggle section="evidence" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayEvidence" label={t('match.display.evidence')} />
        <DisplayToggle section="comment" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayComment" label={t('match.display.comment')} />
        <DisplayToggle section="rating" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayRatingNumeric" label={t('match.display.rating')} defaultEnabled={false} />
        <DisplayToggle section="curation" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCuration" label={t('match.display.curation')} />
        <DisplayToggle section="shuffle" field="enabled" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayShuffle" label={t('match.display.shuffle')} />
      </div>

      <h6 className="mt-2">{t('match.section.display.thresholds')}</h6>
      <div className="row">
        <DisplayNumber section="speedBadge" field="minK" draft={draft} updateDisplay={updateDisplay} tooltipKey="displaySpeedMinK" label={t('match.display.speedMinK')} />
        <DisplayNumber section="speedBadge" field="maxRatioToRunnerUp" draft={draft} updateDisplay={updateDisplay} tooltipKey="displaySpeedMaxRatio" label={t('match.display.speedMaxRatio')} step="0.01" />
        <DisplayNumber section="speedBadge" field="minGapHours" draft={draft} updateDisplay={updateDisplay} tooltipKey="displaySpeedMinGap" label={t('match.display.speedMinGap')} step="0.1" />
        <DisplayNumber section="coverageBadge" field="minTopics" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCoverageMinTopics" label={t('match.display.coverageMinTopics')} />
        <DisplayNumber section="evidence" field="minCompletedProjects" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayEvidenceMinCompleted" label={t('match.display.evidenceMinCompleted')} />
        <DisplayNumber section="curation" field="smallPoolThreshold" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCurationSmallPool" label={t('match.display.curationSmallPool')} />
        <DisplayNumber section="curation" field="allAvailableMinA" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCurationMinA" label={t('match.display.curationMinA')} step="0.01" />
        <DisplayNumber section="precedence" field="maxDistinctionBadgesPerCard" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayMaxBadgesPerCard" label={t('match.display.maxBadgesPerCard')} />
        <DisplayNumber section="precedence" field="maxCardsPerBadge" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayMaxCardsPerBadge" label={t('match.display.maxCardsPerBadge')} allowEmpty />
        <DisplayNumber section="precedence" field="minUnbadgedCards" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayMinUnbadged" label={t('match.display.minUnbadged')} />
        <DisplayNumber section="rating" field="minRatingCount" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayRatingMinCount" label={t('match.display.ratingMinCount')} />
        <DisplayNumber section="rating" field="minRating" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayRatingMinScore" label={t('match.display.ratingMinScore')} step="0.1" />
      </div>

      <div className="row">
        <DisplayToggle section="speedBadge" field="suppressOnTie" draft={draft} updateDisplay={updateDisplay} tooltipKey="displaySpeedSuppressTie" label={t('match.display.speedSuppressTie')} />
        <DisplayToggle section="coverageBadge" field="requireAllTopics" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayCoverageRequireAll" label={t('match.display.coverageRequireAll')} />
        <DisplayToggle section="expertBadge" field="matchAnyClassifiedField" draft={draft} updateDisplay={updateDisplay} tooltipKey="displayExpertAnyField" label={t('match.display.expertAnyField')} />
      </div>
    </section>
  );
}
